/* eslint-disable react/prop-types */
import Swal from 'sweetalert2';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import React, { useCallback, useState } from 'react';
// api
import { getCities } from '../../../api/getCities';
import { getSubCategories } from '../../../api/getSubCategories';
// components
import MobileAdvancedSearch from './components/MobileAdvancedSearch';
import DesktopAdvancedSearch from './components/DesktopAdvancedSearch';
// helpers
import { debounceAsync } from '../../../../helpers';
// hooks
import { useWindowSize } from '../../../../../Hooks/use-window-size';

const sortValueRadioSettings = [
  { value: 'newFirst', text: 'newFirst' },
  { value: 'lowestSalary', text: 'lowestSalary' },
  { value: 'highestSalary', text: 'highestSalary' },
];

const resultsPeriodRadioSettings = [
  { value: 'oneMonth', text: 'oneMonth' },
  { value: 'threeMonths', text: 'threeMonths' },
  { value: 'sixMonths', text: 'sixMonths' },
  { value: 'allTime', text: 'allTime' },
];

const searchLogicRadioSettings = [
  { value: 'all', text: 'searchLogicRadioTxt1' },
  { value: 'any', text: 'searchLogicRadioTxt2' },
  { value: 'exact', text: 'searchLogicRadioTxt3' },
];

const searchPlaceCheckboxSettings = [
  { name: 'everywhere', text: 'searchPlaceEverywhere' },
  { name: 'titles', text: 'searchPlaceCvTitle' },
  { name: 'educations', text: 'searchPlaceEducation' },
  { name: 'experiences', text: 'searchPlaceExperience' },
  { name: 'companiesAndIndustries', text: 'searchPlaceCompanies' },
  { name: 'positions', text: 'searchPlacePositions' },
];

const searchExperienceCheckboxSettings = [
  { name: '0-0', text: 'searchExperienceNoExperience' },
  { name: '1-3', text: 'searchExperienceFrom1To3Years' },
  { name: '3-6', text: 'searchExperienceFrom3To6Years' },
  { name: '6-35', text: 'searchExperienceOver6Years' },
];

const searchWorkTypeCheckboxSettings = [
  { name: '1', text: 'searchWorkTypeFullTime' },
  { name: '2', text: 'searchWorkTypePartTime' },
  { name: '3', text: 'searchWorkTypeRemoteWork' },
  { name: '4', text: 'searchWorkTypeVariableSchedule' },
  { name: '5', text: 'searchWorkTypeFlexibleSchedule' },
  { name: '6', text: 'searchWorkTypeWatchMethod' },
];

const searchGenderCheckboxSettings = [
  { name: '0', text: 'searchGenderFemale' },
  { name: '1', text: 'searchGenderMale' },
];

const searchLocationCheckboxSettings = [
  { name: 'location', text: 'searchLocation' },
  { name: 'relocations', text: 'searchRelocations' },
];

const AdvancedSearchSection = ({ setLoading, searchParams, setSearchParams }) => {
  const { width } = useWindowSize();
  const { t } = useTranslation('global');
  const {
    searchPlace = [],
    searchRegions = [],
    searchDriverLicense = false,
  } = searchParams;

  const [searchAgeTo, setSearchAgeTo] = useState([]);
  const [searchAgeFrom, setSearchAgeFrom] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [searchSalaryTo, setSearchSalaryTo] = useState([]);
  const [searchSalaryFrom, setSearchSalaryFrom] = useState([]);
  const [searchSalaryCurrency, setSearchSalaryCurrency] = useState([]);
  const [errors, setErrors] = useState({ searchRegions: [], searchIndustry: '', searchEducation: '', searchCategories: [], searchLanguages: [] });

  const getCitiesOptions = async (inputValue, index) => {
    if (!searchRegions[index].country.value || inputValue.length < 2) {
      return [];
    }

    try {
      const cities = await getCities(parseInt(searchRegions[index].country.value), inputValue);

      if (cities.data.response?.length === 0) {
        throw new Error('No options found');
      }

      const options = cities.data.response.map(city => ({
        value: city.id,
        label: `${city.name}${city.region && `, ${city.region}`}`,
      }));

      return options;
    } catch (error) {
      console.error('Error fetching cities:', error);

      return [];
    }
  };

  const getSubCategoriesOptions = async (id, index) => {
    if (!id) {
      setSubCategories(prevState => {
        const newSubCategory = [...prevState];
        newSubCategory[index] = [];

        return newSubCategory;
      });

      return;
    }

    setSubCategories(prevState => {
      const newSubCategory = [...prevState];
      newSubCategory[index] = [];

      return newSubCategory;
    });

    try {
      const subCategories = await getSubCategories(id);

      const options = subCategories.data.response.map(position => ({
        value: position.id,
        label: position.name,
      }));

      setSubCategories(prevState => {
        const newSubCategories = [...prevState];
        newSubCategories[index] = options;

        return newSubCategories;
      });
    } catch (error) {
      console.error('Couldn\'t get Sub category data!', error);
    }
  };

  const handleChangeSortValueSearchLogic = (e, type) => {
    setSearchParams({
      ...searchParams,
      [type]: e.target.value,
    });
  };

  const handleChangeSearchPlace = e => {
    const { name, checked } = e.target;

    if (name === 'everywhere') {
      if (checked) {
        setSearchParams({
          ...searchParams,
          searchPlace: ['everywhere'],
        });
      } else {
        setSearchParams({
          ...searchParams,
          searchPlace: [],
        });
      }
    } else {
      const updatedSearchPlace = checked
        ? [...searchPlace.filter(item => item !== 'everywhere'), name]
        : searchPlace.filter(item => item !== name);

      setSearchParams({
        ...searchParams,
        searchPlace: updatedSearchPlace,
      });
    }
  };

  const handleAddSearchItem = type => {
    const newItem = {
      searchRegions: { cities: [], country: null },
      searchLanguages: { lng: null, lng_skill: null },
      searchCategories: { category: null, sub_categories: [] },
    };

    setSearchParams(prevState => ({
      ...prevState,
      [type]: [...prevState[type], newItem[type]],
    }));
  };

  const handleChangeRegionsCategoriesLanguages = (type, field, selectedOption, index, limit = null) => {
    if (limit && selectedOption.length > limit) {
      setErrors(prevErrors => {
        const updatedErrors = { ...prevErrors };

        if (!updatedErrors[type][index]) {
          updatedErrors[type][index] = {};
        }

        updatedErrors[type][index][field] = t('jobseekerListing.optionsLimit');

        return updatedErrors;
      });

      return;
    }

    setErrors(prevErrors => {
      const updatedErrors = { ...prevErrors };

      if (updatedErrors[type][index]) {
        updatedErrors[type][index][field] = '';
      }

      return updatedErrors;
    });

    setSearchParams(prevState => {
      const updatedItems = [...prevState[type]];

      switch (field) {
        case 'country':
          updatedItems[index].country = selectedOption;
          updatedItems[index].cities = [];

          return {
            ...prevState,
            [type]: updatedItems,
            searchLocation: prevState.searchLocation.length > 0
              ? prevState.searchLocation
              : [...prevState.searchLocation, 'location'],
          };

        case 'category':
          updatedItems[index].category = selectedOption;
          updatedItems[index].sub_categories = [];
          break;

        case 'lng':
          updatedItems[index].lng = selectedOption;
          updatedItems[index].lng_skill = null;
          break;

        case 'cities':
          updatedItems[index].cities = selectedOption;
          break;

        case 'sub_categories':
          updatedItems[index].sub_categories = selectedOption;
          break;

        case 'lng_skill':
          updatedItems[index].lng_skill = selectedOption;
          break;

        default:
          return prevState;
      }

      return {
        ...prevState,
        [type]: updatedItems,
      };
    });
  };

  const handleRemoveSearchParam = (type, index) => {
    setSearchParams(prevState => ({
      ...prevState,
      [type]: prevState[type].filter((_, i) => i !== index),
    }));

    if (type === 'searchCategories') {
      setSubCategories(prevState => prevState.filter((_, i) => i !== index));
    }
  };

  const handleChangeAgeAndSalary = useCallback(e => {
    const { name, value } = e.target;

    if (name === 'searchAgeTo') {
      setSearchAgeTo(value);
    } else if (name === 'searchAgeFrom') {
      setSearchAgeFrom(value);
    } else if (name === 'searchSalaryTo') {
      setSearchSalaryTo(value);
    } else if (name === 'searchSalaryFrom') {
      setSearchSalaryFrom(value);
    } else {
      setSearchSalaryCurrency(value);
    }

    debouncedHandleChangeAgeAndSalary(name, value);
  }, []); // eslint-disable-line

  const handleKeyDown = e => {
    const isNumberKey = /\d/.test(e.key);
    const isAllowedKey = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key);

    if (!isNumberKey && !isAllowedKey) {
      e.preventDefault();
    }
  };

  const handleChangeExperienceWorkTypeGender = (e, type) => {
    const { name, checked } = e.target;

    setSearchParams(prevState => ({
      ...prevState,
      [type]: checked
        ? [...prevState[type], name]
        : prevState[type].filter(item => item !== name),
    }));
  };

  const handleChangeIndustryEducationDriverCategories = (type, selectedOptions, limit = null) => {
    if (limit && selectedOptions.length > limit) {
      setErrors(prev => ({
        ...prev,
        [type]: t('jobseekerListing.optionsLimit'),
      }));

      return;
    }

    setSearchParams(prevState => ({
      ...prevState,
      [type]: selectedOptions,
    }));

    setErrors(prev => ({
      ...prev,
      [type]: '',
    }));
  };

  const handleChangeSearchDriverLicense = e => {
    const { checked } = e.target;

    setSearchParams({
      ...searchParams,
      searchDriverLicense: checked,
      searchDriverCategories: searchDriverLicense !== checked && [],
    });
  };

  const handleResetFilters = async () => {
    try {
      const confirmationResult = await Swal.fire({
        icon: 'question',
        showCancelButton: true,
        cancelButtonText: t('jobseekerListing.no'),
        confirmButtonText: t('jobseekerListing.yes'),
        title: t('jobseekerListing.resetFiltersTitle'),
      });

      if (confirmationResult.isConfirmed) {
        setLoading(true);

        await new Promise(resolve => setTimeout(resolve, 0));

        setSearchParams(prev => ({
          ...prev,
          searchAgeTo: '',
          searchGender: [],
          searchAgeFrom: '',
          searchLocation: [],
          searchLogic: 'all',
          searchIndustry: [],
          searchSalaryTo: '',
          searchWorkType: [],
          searchEducation: [],
          searchExperience: [],
          searchSalaryFrom: '',
          searchPlace: ['titles'],
          searchSalaryCurrency: '',
          searchDriverCategories: [],
          searchDriverLicense: false,
          searchRegions: [{ country: null, cities: [] }],
          searchLanguages: [{ lng: null, lng_skill: null }],
          searchCategories: [{ category: null, sub_categories: [] }],
        }));
      }

      setLoading(false);

    } catch (error) {
      setLoading(false);

      console.error('Error resetting filters:', error);
    }
  };

  const debouncedGetCitiesOptions = debounceAsync(getCitiesOptions, 500);
  const debouncedHandleChangeAgeAndSalary = debounce((name, value) => {
    setSearchParams(prevSearchParams => ({
      ...prevSearchParams,
      [name]: value,
    }));
  }, 500);

  return (
    width < 992
      ? (
        <MobileAdvancedSearch
          errors={errors}
          searchAgeTo={searchAgeTo}
          searchParams={searchParams}
          handleKeyDown={handleKeyDown}
          searchAgeFrom={searchAgeFrom}
          subCategories={subCategories}
          searchSalaryTo={searchSalaryTo}
          searchSalaryFrom={searchSalaryFrom}
          handleResetFilters={handleResetFilters}
          handleAddSearchItem={handleAddSearchItem}
          searchSalaryCurrency={searchSalaryCurrency}
          sortValueRadioSettings={sortValueRadioSettings}
          getSubCategoriesOptions={getSubCategoriesOptions}
          handleChangeSearchPlace={handleChangeSearchPlace}
          handleRemoveSearchParam={handleRemoveSearchParam}
          handleChangeAgeAndSalary={handleChangeAgeAndSalary}
          searchLogicRadioSettings={searchLogicRadioSettings}
          debouncedGetCitiesOptions={debouncedGetCitiesOptions}
          resultsPeriodRadioSettings={resultsPeriodRadioSettings}
          searchPlaceCheckboxSettings={searchPlaceCheckboxSettings}
          searchGenderCheckboxSettings={searchGenderCheckboxSettings}
          searchLocationCheckboxSettings={searchLocationCheckboxSettings}
          searchWorkTypeCheckboxSettings={searchWorkTypeCheckboxSettings}
          handleChangeSearchDriverLicense={handleChangeSearchDriverLicense}
          handleChangeSortValueSearchLogic={handleChangeSortValueSearchLogic}
          searchExperienceCheckboxSettings={searchExperienceCheckboxSettings}
          handleChangeExperienceWorkTypeGender={handleChangeExperienceWorkTypeGender}
          handleChangeRegionsCategoriesLanguages={handleChangeRegionsCategoriesLanguages}
          handleChangeIndustryEducationDriverCategories={handleChangeIndustryEducationDriverCategories}
        />
      )
      : (
        <DesktopAdvancedSearch
          errors={errors}
          searchAgeTo={searchAgeTo}
          searchParams={searchParams}
          handleKeyDown={handleKeyDown}
          searchAgeFrom={searchAgeFrom}
          subCategories={subCategories}
          searchSalaryTo={searchSalaryTo}
          searchSalaryFrom={searchSalaryFrom}
          handleResetFilters={handleResetFilters}
          handleAddSearchItem={handleAddSearchItem}
          searchSalaryCurrency={searchSalaryCurrency}
          sortValueRadioSettings={sortValueRadioSettings}
          getSubCategoriesOptions={getSubCategoriesOptions}
          handleChangeSearchPlace={handleChangeSearchPlace}
          handleRemoveSearchParam={handleRemoveSearchParam}
          handleChangeAgeAndSalary={handleChangeAgeAndSalary}
          searchLogicRadioSettings={searchLogicRadioSettings}
          debouncedGetCitiesOptions={debouncedGetCitiesOptions}
          resultsPeriodRadioSettings={resultsPeriodRadioSettings}
          searchPlaceCheckboxSettings={searchPlaceCheckboxSettings}
          searchGenderCheckboxSettings={searchGenderCheckboxSettings}
          searchLocationCheckboxSettings={searchLocationCheckboxSettings}
          searchWorkTypeCheckboxSettings={searchWorkTypeCheckboxSettings}
          handleChangeSearchDriverLicense={handleChangeSearchDriverLicense}
          handleChangeSortValueSearchLogic={handleChangeSortValueSearchLogic}
          searchExperienceCheckboxSettings={searchExperienceCheckboxSettings}
          handleChangeExperienceWorkTypeGender={handleChangeExperienceWorkTypeGender}
          handleChangeRegionsCategoriesLanguages={handleChangeRegionsCategoriesLanguages}
          handleChangeIndustryEducationDriverCategories={handleChangeIndustryEducationDriverCategories}
        />
      )
  );
};

export default AdvancedSearchSection;
